import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { OrgAvatar } from '@/features/OrgLayout/primitives/OrgAvatar';
import { MessageThread } from '@/features/Messages/MessageThread';
import { cn } from '@/lib/cn';
import { useTrainerMessageThread, useSendTrainerMessage } from '@/lib/hooks/useTrainerMessages';
import { REASON_LABELS, type TrainerMessageReason } from '@/lib/api/trainerMessages';
import { queryKeys } from '@/lib/queryKeys';
import type { OrgTrainer } from '@/lib/api/org';

const REASONS: TrainerMessageReason[] = ['attendance', 'session_materials', 'grading', 'other'];

interface Props {
  batchId: string;
  trainers: OrgTrainer[];
  open: boolean;
  onOpenChange: (v: boolean) => void;
  prefill?: { reason: TrainerMessageReason; body: string };
}

export function MessageTrainerDialog({ batchId, trainers, open, onOpenChange, prefill }: Props) {
  const qc = useQueryClient();
  const [trainerId, setTrainerId] = useState<string>(trainers[0]?.user_id ?? '');
  const [reason, setReason] = useState<TrainerMessageReason | undefined>(prefill?.reason);
  const [draft, setDraft] = useState(prefill?.body ?? '');

  useEffect(() => {
    if (!open) return;
    setTrainerId(trainers[0]?.user_id ?? '');
    setReason(prefill?.reason);
    setDraft(prefill?.body ?? '');
  }, [open, trainers, prefill]);

  const trainer = trainers.find((t) => t.user_id === trainerId) ?? null;
  const { data, isLoading } = useTrainerMessageThread(batchId, trainerId, open && !!trainerId);
  const send = useSendTrainerMessage([...queryKeys.trainerMessages.thread(batchId, trainerId)]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Message trainer</DialogTitle>
        </DialogHeader>

        {trainers.length === 0 ? (
          <p className="py-6 text-center text-sm text-[#9ca3af]">No trainers assigned to this batch yet.</p>
        ) : (
          <>
            {trainers.length > 1 && (
              <select
                value={trainerId}
                onChange={(e) => setTrainerId(e.target.value)}
                className="h-9 w-full rounded-lg border border-[#ececec] bg-white px-3 text-sm text-[#111827]"
                aria-label="Select trainer"
              >
                {trainers.map((t) => (
                  <option key={t.user_id} value={t.user_id}>{t.name}</option>
                ))}
              </select>
            )}

            {trainer && trainers.length === 1 && (
              <div className="flex items-center gap-2 pb-1">
                <OrgAvatar name={trainer.name} size={28} />
                <span className="text-sm font-medium text-[#111827]">{trainer.name}</span>
              </div>
            )}

            <div className="flex flex-wrap gap-1.5 pb-1">
              {REASONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setReason(reason === r ? undefined : r)}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs font-medium',
                    reason === r
                      ? 'border-[#ff5a1f] text-[#ff5a1f]'
                      : 'border-[#ececec] text-[#6b7280] hover:bg-[#fafafa]',
                  )}
                >
                  {REASON_LABELS[r]}
                </button>
              ))}
            </div>

            <MessageThread
              messages={data?.messages ?? []}
              self={data?.self ?? ''}
              loading={isLoading}
              sending={send.isPending}
              emptyText={trainer ? `Start a conversation with ${trainer.name}.` : 'Start a conversation.'}
              initialDraft={draft}
              onSend={(body) => {
                send.mutate(
                  { batch_id: batchId, trainer_id: trainerId, body, reason },
                  {
                    onSuccess: () => {
                      qc.invalidateQueries({ queryKey: queryKeys.org.batchDetail(batchId) });
                    },
                  },
                );
                setDraft('');
              }}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
